import React, { useState } from "react"
import {
	Button,
	Collapse,
	Descriptions,
	Modal,
	notification,
	Tag,
	Typography,
} from "antd"
import { useDispatch } from "react-redux"
import { GoogleMaps, Icon } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { setLoader, setOrders as setOrdersAction } from "store/actions"
import { deliveryZonesToPolygons, filterZoneToPolygons } from "utils/geojson"
import ChangeStatusButton from "./ChangeStatusButton"
import PackagingEditor from "./SidePanelEditors/PackagingEditor"
import SendBackToPackingEditor from "./SidePanelEditors/SendBackToPackingEditor"

const { Text } = Typography

interface OrderDetailPanelProps {
	orderId: string
	orders: any[]
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	onClose: () => void
	onChangeLot: (orderIds: string[]) => void
	onValidateAddress: (orderId: string) => void
	waitForModalConfirmation: (nextStatus: string) => Promise<boolean> // Function to show confirmation modal and wait for user response
	deliveryZones?: any[]
	filterZone?: { geojson?: any } | null
}

const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
	orderId,
	orders,
	setOrders,
	onClose,
	onChangeLot,
	onValidateAddress,
	waitForModalConfirmation,
	deliveryZones,
	filterZone,
}) => {
	const dispatch = useDispatch()
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
	const [activeView, setActiveView] = useState<
		"main" | "packaging" | "sendBackToPacking"
	>("main")

	const order = orders.find((o) => o._id === orderId)

	if (!order) {
		return (
			<div className="OrderDetailPanel">
				<div className="OrderDetailPanel__empty">
					<p>Order not found</p>
				</div>
			</div>
		)
	}

	const isInAddressConfirmation =
		order.orderFlow?.status === "address_confirmation"
	const isInPacking = order.orderFlow?.status === "packing"
	const isNotDeliveredOrCancelled =
		order.orderFlow?.status === "not_delivered" ||
		order.orderFlow?.status === "cancelled"
	const hasRoute = !!order.orderFlow?.route

	const handleDeleteOrder = async () => {
		dispatch(setLoader(true))

		try {
			const response = await API.delete({
				url: Endpoints.uriOrders(),
				data: { orderIds: [order._id] },
			})

			if (response.ok) {
				const filteredOrders = orders.filter((o) => o._id !== order._id)
				dispatch(setOrdersAction(filteredOrders))
				onClose()

				notification.success({
					message: Strings.sidebar.orders,
					description: response?.data?.message,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.sidebar.orders,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (error) {
			notification.error({
				message: Strings.sidebar.orders,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			dispatch(setLoader(false))
			setShowDeleteConfirmModal(false)
		}
	}

	if (activeView === "packaging") {
		return (
			<div className="OrderDetailPanel">
				<PackagingEditor
					order={order}
					setOrders={setOrders}
					onBack={() => setActiveView("main")}
				/>
			</div>
		)
	}

	if (activeView === "sendBackToPacking") {
		return (
			<div className="OrderDetailPanel">
				<SendBackToPackingEditor
					order={order}
					setOrders={setOrders}
					onBack={() => setActiveView("main")}
				/>
			</div>
		)
	}

	return (
		<div className="OrderDetailPanel">
			<div className="OrderDetailPanel__header">
				<div className="OrderDetailPanel__title">
					<h3>{order.orderId || "Order Details"}</h3>
				</div>
				<Button
					type="text"
					icon={<Icon name="close" />}
					onClick={onClose}
					className="OrderDetailPanel__close"
				/>
			</div>

			<div className="OrderDetailPanel__content">
				<ChangeStatusButton
					orders={[order]}
					setOrders={setOrders}
					waitForModalConfirmation={waitForModalConfirmation}
				/>

				<div className="OrderDetailPanel__actions">
					<div className="OrderDetailPanel__actions-header">
						<Icon name="lightning" />
						{Strings.generic.actions}
					</div>
					<div className="OrderDetailPanel__actions-grid">
						<div
							className="OrderDetailPanel__actions-item"
							onClick={() => window.open(`/orders/${order._id}`, "_blank")}
						>
							<Icon name="external-link" />
							<span>{Strings.orders.openOrder}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!hasRoute ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								hasRoute
									? () =>
											window.open(`/routes/${order.orderFlow.route}`, "_blank")
									: undefined
							}
						>
							<Icon name="route" />
							<span>{Strings.orders.openRoute}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!isInAddressConfirmation ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								isInAddressConfirmation
									? () => onValidateAddress(order.orderId)
									: undefined
							}
						>
							<Icon name="location" />
							<span>{Strings.orders.validateAddress}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!isInAddressConfirmation ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								isInAddressConfirmation
									? () => onChangeLot([order._id])
									: undefined
							}
						>
							<Icon name="package" />
							<span>{Strings.orders.changeLot}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!isInPacking ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								isInPacking ? () => setActiveView("packaging") : undefined
							}
						>
							<Icon name="box" />
							<span>{Strings.orders.packaging}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!isNotDeliveredOrCancelled ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								isNotDeliveredOrCancelled
									? () => setActiveView("sendBackToPacking")
									: undefined
							}
						>
							<Icon name="package" />
							<span>{Strings.orders.sendBackToPacking}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item${!order.label ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								order.label
									? () => window.open(order.label.url, "_blank")
									: undefined
							}
						>
							<Icon name="tag" />
							<span>{Strings.orders.downloadLabel}</span>
						</div>
						<div
							className={`OrderDetailPanel__actions-item OrderDetailPanel__actions-item--danger${!isInAddressConfirmation ? " OrderDetailPanel__actions-item--disabled" : ""}`}
							onClick={
								isInAddressConfirmation
									? () => setShowDeleteConfirmModal(true)
									: undefined
							}
						>
							<Icon name="trash" />
							<span>{Strings.orders.deleteOrder}</span>
						</div>
					</div>
				</div>

				{(() => {
					const boxes: any[] = order.orderFlow?.boxes || []
					const bagId: string | undefined = order.orderFlow?.bagId
					if (!boxes.length && !bagId) return null

					return (
						<div className="OrderDetailPanel__actions">
							<div className="OrderDetailPanel__actions-header">
								<Icon name="box" />
								{Strings.orders.orderInformation}
							</div>
							<div className="OrderDetailPanel__section">
								<Descriptions column={1} bordered size="small">
									{boxes.length > 0 && (
										<Descriptions.Item label={Strings.orders.boxes}>
											{boxes.map((box: any, index: number) => (
												<span key={box.id}>
													{box.id}
													{bagId ? ` (${bagId})` : ""}
													{index < boxes.length - 1 ? ", " : ""}
												</span>
											))}
										</Descriptions.Item>
									)}
									{bagId && !boxes.length && (
										<Descriptions.Item label={Strings.orders.bagId}>
											{bagId}
										</Descriptions.Item>
									)}
									{order.orderFlow?.classificationSource === "bulk" && (
										<Descriptions.Item
											label={Strings.orders.bulkClassificationLabel}
										>
											<Tag color="orange">
												{String(order.orderFlow.bulkClassificationId)
													.slice(-6)
													.toUpperCase()}
											</Tag>
										</Descriptions.Item>
									)}
								</Descriptions>
							</div>
						</div>
					)
				})()}

				{(() => {
					const plannedLat = order.formattedAddress?.location?.lat
					const plannedLng = order.formattedAddress?.location?.lng
					if (typeof plannedLat !== "number" || typeof plannedLng !== "number")
						return null

					const deliveredLat = order.deliveredLocation?.lat
					const deliveredLng = order.deliveredLocation?.lng
					const hasDelivered =
						typeof deliveredLat === "number" && typeof deliveredLng === "number"

					const markers = [
						{
							lat: plannedLat,
							lng: plannedLng,
							label: {
								text: "P",
								color: "white",
								fontSize: "14px",
								fontWeight: "bold",
							},
							icon: {
								path: 0,
								fillColor: "#1890ff",
								fillOpacity: 1,
								scale: 14,
								strokeColor: "white",
								strokeWeight: 2,
							},
						},
						...(hasDelivered
							? [
									{
										lat: deliveredLat as number,
										lng: deliveredLng as number,
										label: {
											text: "D",
											color: "white",
											fontSize: "14px",
											fontWeight: "bold",
										},
										icon: {
											path: 0,
											fillColor: "#52c41a",
											fillOpacity: 1,
											scale: 14,
											strokeColor: "white",
											strokeWeight: 2,
										},
									},
								]
							: []),
					]

					return (
						<div className="OrderDetailPanel__section">
							<Collapse
								ghost
								expandIconPosition="right"
								className="no-padding-collapse"
								defaultActiveKey={
									["deliveryMap"] /* Open by default if map is shown */
								}
							>
								<Collapse.Panel
									header={
										<h4 style={{ margin: 0 }}>
											{Strings.routes.deliveryLocation}
										</h4>
									}
									key="deliveryMap"
								>
									<GoogleMaps
										{...(hasDelivered
											? {
													boundingBox: {
														south: Math.min(plannedLat, deliveredLat as number),
														west: Math.min(plannedLng, deliveredLng as number),
														north: Math.max(plannedLat, deliveredLat as number),
														east: Math.max(plannedLng, deliveredLng as number),
													},
												}
											: {
													zoom: 15,
													center: { lat: plannedLat, lng: plannedLng },
												})}
										mapContainerStyle={{
											width: "100%",
											height: "350px",
											borderRadius: 5,
										}}
										markers={markers}
										polygons={[
											...deliveryZonesToPolygons(deliveryZones),
											...filterZoneToPolygons(filterZone),
										]}
										options={{
											gestureHandling: "cooperative",
											clickableIcons: false,
										}}
									/>
									<div style={{ marginTop: 8, fontSize: 12 }}>
										<div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 6,
												}}
											>
												<div
													style={{
														width: 12,
														height: 12,
														borderRadius: "50%",
														backgroundColor: "#1890ff",
														border: "2px solid white",
														boxShadow: "0 0 2px rgba(0,0,0,0.3)",
													}}
												/>
												<Text type="secondary">
													{Strings.routes.plannedLocation}
												</Text>
											</div>
											{hasDelivered && (
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 6,
													}}
												>
													<div
														style={{
															width: 12,
															height: 12,
															borderRadius: "50%",
															backgroundColor: "#52c41a",
															border: "2px solid white",
															boxShadow: "0 0 2px rgba(0,0,0,0.3)",
														}}
													/>
													<Text type="secondary">
														{Strings.routes.actualDeliveryLocation}
													</Text>
												</div>
											)}
										</div>
									</div>
								</Collapse.Panel>
							</Collapse>
						</div>
					)
				})()}
			</div>

			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center" }}>
						<Icon
							name="warning"
							style={{ color: "#ff4d4f", marginRight: 8, fontSize: 20 }}
						/>
						<span>{Strings.orders.confirmDeleteTitle}</span>
					</div>
				}
				visible={showDeleteConfirmModal}
				onCancel={() => setShowDeleteConfirmModal(false)}
				footer={[
					<Button key="cancel" onClick={() => setShowDeleteConfirmModal(false)}>
						{Strings.generic.cancel}
					</Button>,
					<Button
						key="delete"
						type="primary"
						danger
						onClick={handleDeleteOrder}
					>
						{Strings.orders.deleteOrder}
					</Button>,
				]}
			>
				<div>
					<div
						style={{
							padding: "12px",
							backgroundColor: "#fff1f0",
							border: "1px solid #ffccc7",
							borderRadius: "4px",
							marginBottom: "16px",
						}}
					>
						<div
							style={{
								fontWeight: "bold",
								color: "#cf1322",
								marginBottom: "8px",
							}}
						>
							<span role="img" aria-label="Warning">
								⚠️
							</span>{" "}
							{Strings.orders.confirmDeleteWarning}
						</div>
						<div style={{ color: "#8c8c8c", fontSize: "14px" }}>
							{Strings.orders.confirmDeleteSingleBody.replace(
								"{0}",
								order.orderId
							)}
						</div>
					</div>
				</div>
			</Modal>
		</div>
	)
}

export default OrderDetailPanel
